import {
  Building2,
  Camera,
  Check,
  Clock3,
  Cloud,
  Database,
  LockKeyhole,
  MapPin,
  MessageCircle,
  Plug,
  QrCode,
  RotateCcw,
  Save,
  ShieldCheck,
  Smartphone,
  Volume2,
  WifiOff,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  ConfirmDialog,
  InlineNotice,
  PageHeader,
  StateBadge,
  Toggle,
} from "../components/ui";
import { useApp } from "../state/AppContext";
import type {
  AppSettings,
  AttendanceStatus,
  PermissionMatrix,
} from "../types";
import "../styles/settings.css";

type SettingsTab = "general" | "attendance" | "permissions" | "integrations";

const tabs: Array<{
  id: SettingsTab;
  label: string;
  shortLabel: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    id: "general",
    label: "Umum",
    shortLabel: "Umum",
    description: "Identiti sekolah dan maklumat operasi",
    icon: Building2,
  },
  {
    id: "attendance",
    label: "QR & Kehadiran",
    shortLabel: "QR",
    description: "Peraturan imbasan dan status harian",
    icon: QrCode,
  },
  {
    id: "permissions",
    label: "Akses & Peranan",
    shortLabel: "Akses",
    description: "Kawalan maklumat untuk guru kelas",
    icon: ShieldCheck,
  },
  {
    id: "integrations",
    label: "Integrasi",
    shortLabel: "Integrasi",
    description: "Sambungan luar dan data prototaip",
    icon: Plug,
  },
];

const attendanceStatusOptions: Array<{
  value: AttendanceStatus;
  label: string;
  description: string;
}> = [
  {
    value: "present",
    label: "Hadir",
    description: "Rekod utama selepas imbasan QR berjaya.",
  },
  {
    value: "late",
    label: "Lewat",
    description: "Digunakan apabila murid tiba selepas masa ambang.",
  },
  {
    value: "absent",
    label: "Tidak hadir",
    description: "Boleh ditandakan semasa semakan kelas.",
  },
  {
    value: "excused",
    label: "Bersebab",
    description: "Untuk cuti sakit atau makluman penjaga.",
  },
];

const integrationCards: Array<{
  name: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    name: "WhatsApp penjaga",
    description: "Hantar makluman kehadiran kepada nombor penjaga.",
    icon: MessageCircle,
  },
  {
    name: "Pangkalan data awan",
    description: "Segerakkan rekod antara telefon guru dan portal web.",
    icon: Cloud,
  },
  {
    name: "Sistem sekolah / APDM",
    description: "Eksport atau padankan data dengan sistem rasmi sekolah.",
    icon: Database,
  },
];

function SectionHeading({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="settings-section-heading">
      <span className="settings-section-heading__icon" aria-hidden="true">
        <Icon size={19} />
      </span>
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const {
    session,
    settings,
    students,
    classes,
    login,
    saveSettings,
    resetDemo,
  } = useApp();
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const [draft, setDraft] = useState<AppSettings>(() => settings);
  const [saved, setSaved] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(settings),
    [draft, settings],
  );

  const updateSetting = <Key extends keyof AppSettings>(
    key: Key,
    value: AppSettings[Key],
  ) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(false);
  };

  const updatePermission = <Key extends keyof PermissionMatrix>(
    key: Key,
    value: PermissionMatrix[Key],
  ) => {
    setDraft((current) => ({
      ...current,
      permissions: { ...current.permissions, [key]: value },
    }));
    setSaved(false);
  };

  const updateAttendanceStatus = (
    status: AttendanceStatus,
    enabled: boolean,
  ) => {
    setDraft((current) => {
      if (!enabled && current.attendanceStatuses.length === 1) return current;
      const nextStatuses = enabled
        ? [...new Set([...current.attendanceStatuses, status])]
        : current.attendanceStatuses.filter((item) => item !== status);
      return { ...current, attendanceStatuses: nextStatuses };
    });
    setSaved(false);
  };

  const handleSave = () => {
    saveSettings(draft);
    setSaved(true);
  };

  if (session?.role !== "admin") {
    return (
      <div className="settings-page">
        <PageHeader
          eyebrow="Kawalan sistem"
          title="Tetapan"
          description="Halaman ini dikhaskan untuk pentadbir sekolah."
        />
        <section className="settings-restricted panel">
          <span className="settings-restricted__icon" aria-hidden="true">
            <LockKeyhole size={27} />
          </span>
          <div className="settings-restricted__copy">
            <div className="settings-restricted__title">
              <h2>Akses pentadbir diperlukan</h2>
              <StateBadge state="demo" />
            </div>
            <p>
              Guru kelas boleh mengimbas QR dan mengurus kehadiran kelas sendiri,
              tetapi tidak boleh mengubah peraturan seluruh sekolah.
            </p>
            <Button
              icon={ShieldCheck}
              onClick={() => login("admin")}
            >
              Tukar ke pentadbir demo
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <PageHeader
        eyebrow="Kawalan sistem"
        title="Tetapan sekolah"
        description="Laraskan cara JosephCare merekod kehadiran dan mengawal akses guru."
        actions={
          <Button
            icon={saved && !isDirty ? Check : Save}
            onClick={handleSave}
            disabled={!isDirty}
          >
            {saved && !isDirty ? "Disimpan" : "Simpan perubahan"}
          </Button>
        }
      />

      {saved && !isDirty && (
        <div className="settings-save-notice" role="status">
          <Check size={17} />
          Perubahan disimpan pada peranti ini.
        </div>
      )}

      <div className="settings-layout">
        <nav
          className="settings-tabs"
          aria-label="Kategori tetapan"
          role="tablist"
        >
          {tabs.map(({ id, label, shortLabel, description, icon: Icon }) => (
            <button
              key={id}
              id={`settings-tab-${id}`}
              type="button"
              className="settings-tab"
              role="tab"
              aria-selected={activeTab === id}
              aria-controls={`settings-panel-${id}`}
              onClick={() => setActiveTab(id)}
            >
              <span className="settings-tab__icon" aria-hidden="true">
                <Icon size={18} />
              </span>
              <span className="settings-tab__copy">
                <strong className="settings-tab__label">{label}</strong>
                <strong className="settings-tab__short-label">{shortLabel}</strong>
                <span>{description}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="settings-content">
          {activeTab === "general" && (
            <section
              id="settings-panel-general"
              className="settings-panel panel"
              role="tabpanel"
              aria-labelledby="settings-tab-general"
            >
              <div className="settings-panel__header">
                <SectionHeading
                  icon={Building2}
                  title="Maklumat sekolah"
                  description="Nama ini digunakan pada paparan, laporan dan kad QR."
                />
              </div>
              <div className="settings-panel__body">
                <div className="form-grid">
                  <div className="form-field form-grid__full">
                    <label htmlFor="school-name">Nama penuh sekolah</label>
                    <input
                      id="school-name"
                      value={draft.schoolName}
                      onChange={(event) =>
                        updateSetting("schoolName", event.target.value)
                      }
                      autoComplete="organization"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="school-short-name">Nama ringkas</label>
                    <input
                      id="school-short-name"
                      value={draft.schoolShortName}
                      onChange={(event) =>
                        updateSetting("schoolShortName", event.target.value)
                      }
                    />
                    <span className="form-field__help">
                      Digunakan apabila ruang paparan terhad.
                    </span>
                  </div>
                  <div className="settings-school-scale" aria-label="Skala data demo">
                    <span>
                      <strong>{students.length}</strong>
                      murid
                    </span>
                    <span>
                      <strong>{classes.length}</strong>
                      kelas
                    </span>
                    <span>
                      <strong>2</strong>
                      peranan
                    </span>
                  </div>
                </div>

                <InlineNotice tone="neutral" title="Data prototaip setempat">
                  Maklumat ini disimpan dalam pelayar untuk sesi demonstrasi. Ia
                  belum dihantar ke pelayan sekolah.
                </InlineNotice>
              </div>
            </section>
          )}

          {activeTab === "attendance" && (
            <div
              id="settings-panel-attendance"
              className="settings-panel-stack"
              role="tabpanel"
              aria-labelledby="settings-tab-attendance"
            >
              <section className="settings-panel panel">
                <div className="settings-panel__header">
                  <SectionHeading
                    icon={Clock3}
                    title="Peraturan kehadiran"
                    description="Tetapkan masa lewat dan jeda antara imbasan."
                  />
                </div>
                <div className="settings-panel__body">
                  <div className="form-grid">
                    <div className="form-field">
                      <label htmlFor="late-after">Dikira lewat selepas</label>
                      <input
                        id="late-after"
                        type="time"
                        value={draft.lateAfter}
                        onChange={(event) =>
                          updateSetting("lateAfter", event.target.value)
                        }
                      />
                      <span className="form-field__help">
                        Imbasan selepas waktu ini dicadangkan sebagai “Lewat”.
                      </span>
                    </div>
                    <div className="form-field">
                      <label htmlFor="scan-cooldown">Jeda imbasan (saat)</label>
                      <input
                        id="scan-cooldown"
                        type="number"
                        min={1}
                        max={30}
                        inputMode="numeric"
                        value={draft.scanCooldownSeconds}
                        onChange={(event) =>
                          updateSetting(
                            "scanCooldownSeconds",
                            Math.min(
                              30,
                              Math.max(1, Number(event.target.value) || 1),
                            ),
                          )
                        }
                      />
                      <span className="form-field__help">
                        Mengelakkan kod yang sama dibaca berulang kali.
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="settings-panel panel">
                <div className="settings-panel__header">
                  <SectionHeading
                    icon={Camera}
                    title="Pengalaman mengimbas"
                    description="Kawalan peranti yang digunakan oleh guru."
                  />
                </div>
                <div className="settings-panel__body settings-toggle-list">
                  <Toggle
                    checked={draft.cameraEnabled}
                    onChange={(checked) =>
                      updateSetting("cameraEnabled", checked)
                    }
                    label="Benarkan kamera untuk imbasan"
                    description="Guru boleh membuka kamera daripada telefon atau pelayar web."
                  />
                  <Toggle
                    checked={draft.confirmStudentPhoto}
                    onChange={(checked) =>
                      updateSetting("confirmStudentPhoto", checked)
                    }
                    label="Pengesahan identiti murid"
                    description="Paparkan nama, kelas dan avatar sebelum rekod disahkan."
                  />
                  <Toggle
                    checked={draft.playScanSound}
                    onChange={(checked) =>
                      updateSetting("playScanSound", checked)
                    }
                    label="Bunyi selepas imbasan"
                    description="Mainkan maklum balas audio untuk berjaya atau ralat."
                  />
                  <Toggle
                    checked={draft.offlineEnabled}
                    onChange={(checked) =>
                      updateSetting("offlineEnabled", checked)
                    }
                    label="Sokong rekod luar talian"
                    description="Simpan rekod pada peranti apabila internet terputus."
                  />
                  <Toggle
                    checked={draft.requireLocation}
                    onChange={(checked) =>
                      updateSetting("requireLocation", checked)
                    }
                    label="Minta lokasi semasa mengimbas"
                    description="Untuk prototaip, pilihan ini hanya menyimpan tetapan dan belum mengesahkan geolokasi."
                    demo
                  />
                </div>
              </section>

              <section className="settings-panel panel">
                <div className="settings-panel__header">
                  <SectionHeading
                    icon={QrCode}
                    title="Status yang dibenarkan"
                    description="Sekurang-kurangnya satu status perlu kekal aktif."
                  />
                </div>
                <div className="settings-panel__body settings-toggle-list">
                  {attendanceStatusOptions.map((option) => (
                    <Toggle
                      key={option.value}
                      checked={draft.attendanceStatuses.includes(option.value)}
                      onChange={(checked) =>
                        updateAttendanceStatus(option.value, checked)
                      }
                      label={option.label}
                      description={option.description}
                    />
                  ))}
                </div>
              </section>
            </div>
          )}

          {activeTab === "permissions" && (
            <section
              id="settings-panel-permissions"
              className="settings-panel panel"
              role="tabpanel"
              aria-labelledby="settings-tab-permissions"
            >
              <div className="settings-panel__header">
                <SectionHeading
                  icon={ShieldCheck}
                  title="Matriks akses guru kelas"
                  description="Pentadbir sentiasa mempunyai akses penuh."
                />
              </div>
              <div className="settings-panel__body">
                <InlineNotice tone="info" title="Skop guru dikekalkan">
                  Secara lalai, guru hanya melihat murid dalam kelas yang
                  ditugaskan kepada mereka.
                </InlineNotice>

                <div className="settings-permission-head" aria-hidden="true">
                  <span>Keupayaan</span>
                  <span>Pentadbir</span>
                  <span>Guru</span>
                </div>
                <div className="settings-permission-list">
                  <div className="settings-permission-row">
                    <div>
                      <strong>Lihat maklumat kesihatan</strong>
                      <span>Alahan, keadaan kesihatan dan nota kecemasan.</span>
                    </div>
                    <span className="settings-permission-admin">
                      <Check size={16} /> Ya
                    </span>
                    <label className="settings-permission-check">
                      <input
                        type="checkbox"
                        checked={draft.permissions.teacherCanViewMedical}
                        onChange={(event) =>
                          updatePermission(
                            "teacherCanViewMedical",
                            event.target.checked,
                          )
                        }
                      />
                      <span>
                        {draft.permissions.teacherCanViewMedical ? "Ya" : "Tidak"}
                      </span>
                    </label>
                  </div>
                  <div className="settings-permission-row">
                    <div>
                      <strong>Lihat nombor telefon penjaga</strong>
                      <span>Maklumat hubungan utama dan kecemasan.</span>
                    </div>
                    <span className="settings-permission-admin">
                      <Check size={16} /> Ya
                    </span>
                    <label className="settings-permission-check">
                      <input
                        type="checkbox"
                        checked={
                          draft.permissions.teacherCanViewGuardianPhone
                        }
                        onChange={(event) =>
                          updatePermission(
                            "teacherCanViewGuardianPhone",
                            event.target.checked,
                          )
                        }
                      />
                      <span>
                        {draft.permissions.teacherCanViewGuardianPhone
                          ? "Ya"
                          : "Tidak"}
                      </span>
                    </label>
                  </div>
                  <div className="settings-permission-row">
                    <div>
                      <strong>Ubah rekod kehadiran</strong>
                      <span>Membetulkan status selepas rekod dibuat.</span>
                    </div>
                    <span className="settings-permission-admin">
                      <Check size={16} /> Ya
                    </span>
                    <label className="settings-permission-check">
                      <input
                        type="checkbox"
                        checked={draft.permissions.teacherCanEditAttendance}
                        onChange={(event) =>
                          updatePermission(
                            "teacherCanEditAttendance",
                            event.target.checked,
                          )
                        }
                      />
                      <span>
                        {draft.permissions.teacherCanEditAttendance
                          ? "Ya"
                          : "Tidak"}
                      </span>
                    </label>
                  </div>
                  <div className="settings-permission-row">
                    <div>
                      <strong>Muat turun kad QR</strong>
                      <span>Cetak semula kod milik murid dalam kelas.</span>
                    </div>
                    <span className="settings-permission-admin">
                      <Check size={16} /> Ya
                    </span>
                    <label className="settings-permission-check">
                      <input
                        type="checkbox"
                        checked={draft.permissions.teacherCanDownloadQr}
                        onChange={(event) =>
                          updatePermission(
                            "teacherCanDownloadQr",
                            event.target.checked,
                          )
                        }
                      />
                      <span>
                        {draft.permissions.teacherCanDownloadQr ? "Ya" : "Tidak"}
                      </span>
                    </label>
                  </div>
                  <div className="settings-permission-row">
                    <div>
                      <strong>Lihat kelas lain</strong>
                      <span>Cari profil murid di luar kelas tugasan.</span>
                    </div>
                    <span className="settings-permission-admin">
                      <Check size={16} /> Ya
                    </span>
                    <label className="settings-permission-check">
                      <input
                        type="checkbox"
                        checked={
                          draft.permissions.teacherCanViewOtherClasses
                        }
                        onChange={(event) =>
                          updatePermission(
                            "teacherCanViewOtherClasses",
                            event.target.checked,
                          )
                        }
                      />
                      <span>
                        {draft.permissions.teacherCanViewOtherClasses
                          ? "Ya"
                          : "Tidak"}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === "integrations" && (
            <div
              id="settings-panel-integrations"
              className="settings-panel-stack"
              role="tabpanel"
              aria-labelledby="settings-tab-integrations"
            >
              <InlineNotice tone="warning" title="Paparan demonstrasi sahaja">
                Semua integrasi di bawah belum disambungkan kepada akaun,
                pelayan atau nombor sebenar.
              </InlineNotice>

              <section className="settings-panel panel">
                <div className="settings-panel__header">
                  <SectionHeading
                    icon={Plug}
                    title="Sambungan luar"
                    description="Ruang ini menunjukkan hala tuju integrasi versi produksi."
                  />
                </div>
                <div className="settings-panel__body">
                  <div className="settings-integration-grid">
                    {integrationCards.map(({ name, description, icon: Icon }) => (
                      <article className="settings-integration-card" key={name}>
                        <div className="settings-integration-card__top">
                          <span
                            className="settings-integration-card__icon"
                            aria-hidden="true"
                          >
                            <Icon size={20} />
                          </span>
                          <StateBadge state="demo" />
                        </div>
                        <h3>{name}</h3>
                        <p>{description}</p>
                        <Button variant="secondary" disabled full>
                          Tidak disambung
                        </Button>
                      </article>
                    ))}
                  </div>

                  <div className="settings-integration-automation">
                    <Toggle
                      checked={draft.autoNotifyGuardian}
                      onChange={(checked) =>
                        updateSetting("autoNotifyGuardian", checked)
                      }
                      label="Sediakan automasi notifikasi penjaga"
                      description="Tetapan simulasi ini tidak akan menghantar mesej sebenar sehingga integrasi disambungkan."
                      demo
                    />
                  </div>
                </div>
              </section>

              <section className="settings-reset panel">
                <div>
                  <span className="eyebrow">Kawalan prototaip</span>
                  <h2>Mulakan semula data demo</h2>
                  <p>
                    Pulihkan murid, rekod kehadiran dan tetapan asal untuk
                    demonstrasi seterusnya.
                  </p>
                </div>
                <Button
                  variant="danger"
                  icon={RotateCcw}
                  onClick={() => setResetOpen(true)}
                >
                  Set semula demo
                </Button>
              </section>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={resetDemo}
        title="Set semula semua data demo?"
        message="Semua perubahan lokal akan dipadam, data contoh akan dipulihkan dan sesi semasa akan dilog keluar."
        confirmLabel="Ya, set semula"
        danger
      />

      <div className="settings-device-notes" aria-hidden="true">
        <Smartphone size={16} />
        <Camera size={16} />
        <Volume2 size={16} />
        <WifiOff size={16} />
        <MapPin size={16} />
      </div>
    </div>
  );
}
